import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Denuncia, TipoDenuncia } from 'src/app/shared/model/denuncias/denuncia.model';
import { CategoriaDenuncia } from 'src/app/shared/model/denuncias/categoria-denuncia.model';
import { environment } from 'src/environments/environment';
import { DenunciasService } from '../denuncias.service';
import {
  EVIDENCIA_ACCEPT, EVIDENCIA_RESUMEN, EVIDENCIA_TEXTO, RespuestaAltaDenuncia, formatoTamanio, validarEvidencia
} from '../evidencia-permitida';

declare global {
  interface Window {
    grecaptcha?: {
      render: (container: string | HTMLElement, parameters: Record<string, unknown>) => number;
      reset: (widgetId?: number) => void;
      getResponse: (widgetId?: number) => string;
      ready: (cb: () => void) => void;
    };
  }
}

@Component({
  selector: 'app-alta-denuncia',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './alta-denuncia.component.html',
  styleUrls: ['./alta-denuncia.component.css']
})
export class AltaDenunciaComponent implements OnInit, OnDestroy {

  TipoDenuncia = TipoDenuncia;
  recaptchaSiteKey = environment.recaptchaSiteKey;

  paso: 'modalidad' | 'formulario' = 'modalidad';
  modalidadSeleccionada: TipoDenuncia | null = null;
  tipo: TipoDenuncia | null = null;
  categorias: CategoriaDenuncia[] = [];
  denuncia: Denuncia = new Denuncia();

  archivoNombre: string = '';
  archivoSeleccionado: File | null = null;
  // Lo mismo que exige el servidor: el accept del input y el texto que se ve siempre.
  readonly evidenciaAccept = EVIDENCIA_ACCEPT;
  readonly evidenciaTexto = EVIDENCIA_TEXTO;
  readonly evidenciaResumen = EVIDENCIA_RESUMEN;
  aceptaTerminos: boolean = false;
  mostrarPopupAnonimo: boolean = false;
  cargando: boolean = false;
  errorMensaje: string = '';
  captchaToken: string = '';
  captchaResuelto: boolean = false;
  captchaExpirado: boolean = false;
  captchaError: string = '';
  captchaWidgetId: number | null = null;
  captchaScriptReady: boolean = false;
  captchaScriptLoading: boolean = false;

  constructor(
    private denunciasService: DenunciasService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.cargarCategorias();
  }

  ngOnDestroy(): void {
    this.captchaWidgetId = null;
  }

  cargarCategorias(): void {
    this.denunciasService.consultarCategorias().subscribe(resp => {
      if (resp && resp.list) {
        this.categorias = resp.list;
      }
    });
  }

  seleccionarModalidad(tipo: TipoDenuncia): void {
    this.modalidadSeleccionada = tipo;
  }

  iniciarFormulario(tipo: TipoDenuncia): void {
    this.limpiarWidgetCaptcha();
    this.modalidadSeleccionada = tipo;
    this.denuncia = new Denuncia();
    this.denuncia.tipoDenuncia = tipo;
    this.tipo = tipo;
    this.errorMensaje = '';
    this.archivoNombre = '';
    this.archivoSeleccionado = null;
    this.aceptaTerminos = false;
    this.resetCaptchaState();

    if (tipo === TipoDenuncia.ANONIMA) {
      this.mostrarPopupAnonimo = true;
      return;
    }

    this.paso = 'formulario';
    this.programarInicializacionCaptcha();
  }

  cerrarPopup(): void {
    this.mostrarPopupAnonimo = false;
    this.paso = 'formulario';
    this.programarInicializacionCaptcha();
  }

  private programarInicializacionCaptcha(): void {
    setTimeout(() => this.inicializarCaptcha(), 0);
  }

  cambiarModalidad(): void {
    this.limpiarWidgetCaptcha();
    this.paso = 'modalidad';
    this.modalidadSeleccionada = null;
    this.tipo = null;
    this.denuncia = new Denuncia();
    this.archivoNombre = '';
    this.archivoSeleccionado = null;
    this.aceptaTerminos = false;
    this.errorMensaje = '';
    this.mostrarPopupAnonimo = false;
    this.resetCaptchaState();
  }

  private limpiarWidgetCaptcha(): void {
    if (this.captchaWidgetId !== null && window.grecaptcha) {
      try {
        window.grecaptcha.reset(this.captchaWidgetId);
      } catch (error) {
        console.warn('[recaptcha] reset warning', error);
      }
    }
    const anonimo = document.getElementById('recaptcha-widget-anonimo');
    const confidencial = document.getElementById('recaptcha-widget-confidencial');
    if (anonimo) { anonimo.innerHTML = ''; }
    if (confidencial) { confidencial.innerHTML = ''; }
  }

  onArchivoSeleccionado(event: any): void {
    const file: File | undefined = event.target.files[0];
    if (!this.tomarArchivo(file)) {
      event.target.value = '';
    }
  }

  // La tarjeta dice "o arrastrar": sin esto, soltar el archivo hacía que el
  // navegador lo abriera y el denunciante perdía el formulario.
  onArrastrarArchivo(event: DragEvent): void {
    event.preventDefault();
  }

  onSoltarArchivo(event: DragEvent): void {
    event.preventDefault();
    this.tomarArchivo(event.dataTransfer?.files?.[0]);
    this.cdr.detectChanges();
  }

  /** Extensión y tamaño de la lista blanca; false si se rechazó. */
  private tomarArchivo(file: File | undefined | null): boolean {
    if (!file) {
      this.archivoNombre = '';
      this.archivoSeleccionado = null;
      return true;
    }
    const error = validarEvidencia(file);
    if (error) {
      this.errorMensaje = error;
      this.archivoNombre = '';
      this.archivoSeleccionado = null;
      return false;
    }
    this.errorMensaje = '';
    this.archivoNombre = file.name;
    this.archivoSeleccionado = file;
    return true;
  }

  /** "nombre · 1.2 MB" con archivo; el resumen de formatos sin él. */
  get archivoMeta(): string {
    return this.archivoSeleccionado
      ? `Archivo listo para enviar · ${formatoTamanio(this.archivoSeleccionado.size)}`
      : this.evidenciaResumen;
  }

  onTelefonoInput(event: any): void {
    const soloDigitos = String(event.target.value || '').replace(/\D/g, '').slice(0, 10);
    this.denuncia.telefono = soloDigitos;
    event.target.value = soloDigitos;
  }

  enviar(): void {
    if (!this.tipo) {
      this.errorMensaje = 'Selecciona una modalidad de reporte.';
      return;
    }

    if (!this.denuncia.id_categoria || !this.denuncia.hechos || !this.aceptaTerminos) {
      this.errorMensaje = 'Por favor completa los campos obligatorios.';
      return;
    }
    if (!this.recaptchaSiteKey) {
      this.errorMensaje = 'El captcha no está configurado para este ambiente. Contacte al administrador.';
      return;
    }
    if (!this.captchaResuelto || !this.captchaToken) {
      this.errorMensaje = this.captchaError || 'Por favor valida el captcha antes de enviar la denuncia.';
      return;
    }
    if (this.tipo === TipoDenuncia.CONFIDENCIAL &&
      (!this.denuncia.nombre || !this.denuncia.apellidos || !this.denuncia.email)) {
      this.errorMensaje = 'Para denuncia confidencial, nombre, apellidos y email son obligatorios.';
      return;
    }
    if (this.tipo === TipoDenuncia.CONFIDENCIAL) {
      const emailValido = /^[^\s@]+@[^\s@][^\s@.]*\.[^\s@]+$/.test((this.denuncia.email || '').trim());
      if (!emailValido) {
        this.errorMensaje = 'Ingresa un correo electrónico válido.';
        return;
      }
      if (this.denuncia.telefono && !/^\d{10}$/.test(this.denuncia.telefono)) {
        this.errorMensaje = 'El teléfono debe contener exactamente 10 dígitos.';
        return;
      }
    }

    this.errorMensaje = '';
    this.cargando = true;

    this.denuncia.tipoDenuncia = this.tipo;
    this.denuncia.captchaToken = this.captchaToken;

    this.denunciasService.registrarDenuncia(this.denuncia).subscribe(
      resp => {
        if (resp && resp.pCodRetorno === '0') {
          this.finalizarRegistro(resp);
        } else {
          this.cargando = false;
          this.errorMensaje = resp?.pMensaje || 'Ocurrió un error al registrar la denuncia.';
          if (this.debeResetearCaptcha(resp?.pMensaje)) {
            this.resetCaptchaWidget();
          }
        }
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      },
      () => {
        this.cargando = false;
        this.errorMensaje = 'Error de conexión. Intente más tarde.';
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    );
  }

  cancelar(): void {
    this.router.navigate(['/denuncia/inicio']);
  }

  private finalizarRegistro(resp: RespuestaAltaDenuncia): void {
    if (!this.archivoSeleccionado) {
      this.cargando = false;
      this.irAConfirmacion(resp.codigoSeguimiento);
      return;
    }

    // La evidencia se ata a ESTA denuncia con el token que devolvió el alta;
    // el servidor ya no acepta un id_denuncia cualquiera.
    const formData = new FormData();
    formData.append('file', this.archivoSeleccionado, this.archivoSeleccionado.name);
    formData.append('codigo', resp.codigoSeguimiento || '');
    formData.append('tokenCarga', resp.tokenCarga || '');

    this.denunciasService.uploadEvidencia(formData).subscribe({
      next: evidenciaResp => {
        this.cargando = false;

        if (evidenciaResp?.pCodRetorno === '0') {
          this.irAConfirmacion(resp.codigoSeguimiento);
          return;
        }

        this.irAConfirmacion(
          resp.codigoSeguimiento,
          evidenciaResp?.pMensaje || 'La denuncia se registró, pero no fue posible guardar la evidencia adjunta.'
        );
      },
      error: () => {
        this.cargando = false;
        this.irAConfirmacion(
          resp.codigoSeguimiento,
          'La denuncia se registró, pero ocurrió un error al guardar la evidencia adjunta.'
        );
      }
    });
  }

  private irAConfirmacion(codigoSeguimiento: string, uploadWarning?: string): void {
    this.router.navigate(['/denuncia/confirmacion'], {
      state: { codigoSeguimiento, uploadWarning }
    });
  }

  private inicializarCaptcha(): void {
    if (!this.recaptchaSiteKey) {
      this.captchaError = 'Captcha no configurado.';
      this.cdr.detectChanges();
      return;
    }

    this.cargarScriptCaptcha().then(() => {
      this.esperarGrecaptchaListo(() => this.renderCaptcha());
    }).catch(() => {
      this.captchaError = 'No fue posible cargar el captcha.';
      this.cdr.detectChanges();
    });
  }

  private esperarGrecaptchaListo(cb: () => void, intento: number = 0): void {
    if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
      if (typeof window.grecaptcha.ready === 'function') {
        window.grecaptcha.ready(cb);
      } else {
        cb();
      }
      return;
    }
    if (intento >= 40) {
      this.captchaError = 'No fue posible inicializar el captcha.';
      this.cdr.detectChanges();
      return;
    }
    setTimeout(() => this.esperarGrecaptchaListo(cb, intento + 1), 100);
  }

  private cargarScriptCaptcha(): Promise<void> {
    if (this.captchaScriptReady && window.grecaptcha) {
      return Promise.resolve();
    }

    if (this.captchaScriptLoading) {
      return new Promise((resolve, reject) => {
        const waitForCaptcha = () => {
          if (this.captchaScriptReady && window.grecaptcha) {
            resolve();
            return;
          }
          if (!this.captchaScriptLoading) {
            reject(new Error('El captcha no terminó de cargar.'));
            return;
          }
          setTimeout(waitForCaptcha, 100);
        };
        waitForCaptcha();
      });
    }

    this.captchaScriptLoading = true;
    return new Promise((resolve, reject) => {
      const scriptId = 'google-recaptcha-script';
      const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
      if (existingScript) {
        if (window.grecaptcha) {
          this.captchaScriptLoading = false;
          this.captchaScriptReady = true;
          resolve();
          return;
        }
        existingScript.addEventListener('load', () => {
          this.captchaScriptLoading = false;
          this.captchaScriptReady = true;
          resolve();
        }, { once: true });
        existingScript.addEventListener('error', () => {
          this.captchaScriptLoading = false;
          reject(new Error('Falló el script del captcha que ya estaba en la página.'));
        }, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.captchaScriptLoading = false;
        this.captchaScriptReady = true;
        resolve();
      };
      script.onerror = () => {
        this.captchaScriptLoading = false;
        reject(new Error('No se pudo cargar el script del captcha.'));
      };
      document.body.appendChild(script);
    });
  }

  private renderCaptcha(intento: number = 0): void {
    const container = document.getElementById(this.captchaContainerId());
    if (!container || !window.grecaptcha) {
      if (intento < 30) {
        setTimeout(() => this.renderCaptcha(intento + 1), 100);
        return;
      }
      this.captchaError = 'No fue posible mostrar el captcha. Recargue la pagina.';
      this.cdr.detectChanges();
      return;
    }

    container.innerHTML = '';
    try {
      this.captchaWidgetId = window.grecaptcha.render(container, {
        sitekey: this.recaptchaSiteKey,
        callback: (token: string) => this.onCaptchaResolved(token),
        'expired-callback': () => this.onCaptchaExpired(),
        'error-callback': () => this.onCaptchaFailed()
      });
    } catch (error: any) {
      console.error('[recaptcha] render error', error);
      const mensajeDetalle = error && error.message ? ' (' + error.message + ')' : '';
      this.captchaError = 'No fue posible mostrar el captcha.' + mensajeDetalle;
    }
    this.cdr.detectChanges();
  }

  private captchaContainerId(): string {
    return this.tipo === TipoDenuncia.CONFIDENCIAL
      ? 'recaptcha-widget-confidencial'
      : 'recaptcha-widget-anonimo';
  }

  private onCaptchaResolved(token: string): void {
    this.captchaToken = token;
    this.captchaResuelto = true;
    this.captchaExpirado = false;
    this.captchaError = '';
    this.errorMensaje = '';
    this.cdr.detectChanges();
  }

  private onCaptchaExpired(): void {
    this.captchaToken = '';
    this.captchaResuelto = false;
    this.captchaExpirado = true;
    this.captchaError = 'El captcha expiró. Vuelve a validarlo.';
    this.cdr.detectChanges();
  }

  private onCaptchaFailed(): void {
    this.captchaToken = '';
    this.captchaResuelto = false;
    this.captchaError = 'No fue posible validar el captcha. Intenta nuevamente.';
    this.cdr.detectChanges();
  }

  private resetCaptchaWidget(): void {
    const widgetId = this.captchaWidgetId;
    this.resetCaptchaState();
    if (window.grecaptcha && widgetId !== null) {
      window.grecaptcha.reset(widgetId);
    }
    this.cdr.detectChanges();
  }

  private resetCaptchaState(): void {
    this.denuncia.captchaToken = '';
    this.captchaToken = '';
    this.captchaResuelto = false;
    this.captchaExpirado = false;
    this.captchaError = '';
    this.captchaWidgetId = null;
  }

  private debeResetearCaptcha(mensaje?: string): boolean {
    const contenido = (mensaje || '').toLowerCase();
    return contenido.includes('captcha') || contenido.includes('robot');
  }
}
